import { createElement, createContext, useRef, useCallback, useContext, useEffect } from 'react';
import { Modal } from '../../Modal';
import { BaseDialogLayout } from './BaseDialogLayout';
import bundle from '../NovaComponents-Dialog-gui-icu.properties';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { DialogAction } from '../__internal__';
import useHandleDialogOpenState from './useHandleDialogOpenState';
import { postfixTestId } from '../../../util/testId';
import { BaseDialogProps } from '.';

const useTextLocalizer = createUseTextLocalizer(bundle);

type DismissActionHandle = DialogAction['onPress'];

const ParentChildRelContext = createContext<Set<DismissActionHandle> | undefined>(undefined);
const ParentChildRelProvider = ParentChildRelContext.Provider;

export const BaseDialog = (props: BaseDialogProps) => {
  // workaround for NOVACOMMONS-1083
  // TODO This may be redundant now with NOVACOMMONS-2291, not sure I have time to test.
  useTextLocalizer();

  const isModal = props.isModal ?? true;
  const { isOpen, onOpen, onClose, ...restProps } = props;

  const { dialogRef } = useHandleDialogOpenState({
    onOpen,
    onClose,
    isOpen,
  });

  const selfRegistry = useRef<Set<DismissActionHandle>>(new Set()).current;

  const parentRegistry = useContext(ParentChildRelContext);

  const origDismissActionHandle = props.dismissAction?.onPress;
  const origFirstActionHandle = props.firstAction?.onPress;
  const origSecondActionHandle = props.secondAction?.onPress;

  const proxyDismissHandle = useCallback(() => {
    selfRegistry.forEach((handler) => handler?.apply(null));
    origDismissActionHandle?.apply(null);
  }, [selfRegistry, origDismissActionHandle]);

  const proxyFirstActionHandle = useCallback(() => {
    selfRegistry.forEach((handler) => handler?.apply(null));
    origFirstActionHandle?.apply(null);
  }, [selfRegistry, origFirstActionHandle]);

  const proxySecondActionHandle = useCallback(() => {
    selfRegistry.forEach((handler) => handler?.apply(null));
    origSecondActionHandle?.apply(null);
  }, [selfRegistry, origSecondActionHandle]);

  const dialogLayoutProps = {
    ...restProps,
    ...(restProps.dismissAction &&
      restProps.dismissAction.onPress && {
        dismissAction: { ...restProps.dismissAction, onPress: proxyDismissHandle },
      }),
    ...(restProps.firstAction &&
      restProps.firstAction.onPress && {
        firstAction: { ...restProps.firstAction, onPress: proxyFirstActionHandle },
      }),
    ...(restProps.secondAction &&
      restProps.secondAction.onPress && {
        secondAction: { ...restProps.secondAction, onPress: proxySecondActionHandle },
      }),
  };

  useEffect(() => {
    if (!isModal) {
      parentRegistry?.add(proxyDismissHandle);
      return () => {
        parentRegistry?.delete(proxyDismissHandle);
      };
    }
  }, [isModal, parentRegistry, proxyDismissHandle]);

  return (
    <Modal
      id={props.id}
      isOpen={props.isOpen}
      showBlockLayer={isModal}
      focusLockProps={props.focusLockProps}
      onContextMenu={props.onContextMenu}
      testId={postfixTestId(props.testId, 'modal')}
      hideAnimation={props.hideAnimation ?? false}
    >
      <ParentChildRelProvider value={selfRegistry}>
        {props.renderLayout ? (
          props.renderLayout()
        ) : (
          <BaseDialogLayout
            ref={dialogRef}
            isOpen={props.isOpen}
            hideAnimation={props.hideAnimation}
            isModal={isModal}
            {...dialogLayoutProps}
          />
        )}
      </ParentChildRelProvider>
    </Modal>
  );
};
